import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, User, Building2, Target, Calendar, FileText, MapPin, Phone, Mail } from 'lucide-react';

/**
 * EntityDataList - Displays entity data as a compact list format
 * Used when there are 5-20 items to show a summary list
 */
const EntityDataList = ({ entityType, data = [], totalCount = 0, filterColumns = [], filterInfo = [], displayFields = [] }) => {
  const navigate = useNavigate();
  
  // Show "no data found" message if totalCount is 0 or data is empty
  if (totalCount === 0 || !data || data.length === 0) {
    const displayType = entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Entity';
    return (
      <div className="entity-data-list my-4">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
          <div className="text-gray-500 dark:text-gray-400 text-sm">
            <p className="font-medium mb-1">No {displayType} found</p>
            <p className="text-xs">No data matches your search criteria.</p>
          </div>
        </div>
      </div>
    );
  }

  // Icon mapping for entity types
  const entityIcons = {
    customer: User,
    lead: Target,
    opportunity: Target,
    activity: Calendar,
    note: FileText,
    contact: User,
    company: Building2
  };

  // Get entity name for display
  const getEntityName = (item, type, filterColumns = []) => {
    const typeLower = (type || '').toLowerCase();
    switch (typeLower) {
      case 'customer':
        // Always show FullName (FirstName + LastName) with Nickname if available
        let name = '';
        if (item.FullName) {
          name = item.NickName ? `${item.FullName} (${item.NickName})` : item.FullName;
        } else if (item.FirstName || item.LastName) {
          const fullName = `${item.FirstName || ''} ${item.LastName || ''}`.trim();
          name = item.NickName ? `${fullName} (${item.NickName})` : fullName || item.CompanyName || `Customer ${item.CustomerID}`;
        } else {
          name = item.CustomerName || item.Name || item.CompanyName || `Customer ${item.CustomerID}`;
        }
        
        // Append filter column values if they exist
        const filterValues = [];
        if (filterColumns.includes('Email') && item.Email) {
          filterValues.push(`Email: ${item.Email}`);
        }
        if (filterColumns.includes('Phone') && item.Phone) {
          filterValues.push(`Phone: ${item.Phone}`);
        }
        if (filterColumns.includes('NickName') && item.NickName) {
          filterValues.push(`Nickname: ${item.NickName}`);
        }
        
        return filterValues.length > 0 ? `${name} - ${filterValues.join(', ')}` : name;
      case 'lead':
        // Show Title as primary, with ContactName/CompanyName
        let leadName = item.Title || `Lead ${item.LeadID}`;
        const contactInfo = item.ContactName || item.CompanyName || item.Company;
        if (contactInfo) {
          leadName = `${leadName} - ${contactInfo}`;
        }
        
        // Append filter column values if they exist
        const leadFilterValues = [];
        if (filterColumns.includes('Email') && item.Email) {
          leadFilterValues.push(`Email: ${item.Email}`);
        }
        if (filterColumns.includes('Phone') && item.Phone) {
          leadFilterValues.push(`Phone: ${item.Phone}`);
        }
        if ((filterColumns.includes('EstimatedBudget') || filterColumns.includes('EstimatedValue')) && (item.EstimatedBudget || item.EstimatedValue)) {
          const value = item.EstimatedBudget || item.EstimatedValue;
          const displayValue = typeof value === 'number' ? `$${value.toLocaleString()}` : value;
          leadFilterValues.push(`Value: ${displayValue}`);
        }
        
        return leadFilterValues.length > 0 ? `${leadName} - ${leadFilterValues.join(', ')}` : leadName;
      case 'opportunity':
        return item.OpportunityName || item.Name || `Opportunity ${item.OpportunityID}`;
      case 'activity':
        return item.Subject || item.Name || `${item.ActivityType || 'Activity'} ${item.ActivityID}`;
      case 'note':
        return item.Subject || item.Title || `Note ${item.NoteID}`;
      default:
        return item.Name || item.Subject || `${type} ${item.ID || ''}`;
    }
  };

  // Get entity ID and route
  const getEntityRoute = (item, type) => {
    const typeLower = (type || '').toLowerCase();
    const idField = typeLower === 'customer' ? 'CustomerID' :
                    typeLower === 'lead' ? 'LeadID' :
                    typeLower === 'opportunity' ? 'OpportunityID' :
                    typeLower === 'activity' ? 'ActivityID' :
                    typeLower === 'note' ? 'NoteID' :
                    Object.keys(item).find(k => k.toLowerCase().endsWith('id'));
    
    const id = item[idField];
    if (!id) return null;
    
    const route = typeLower === 'customer' ? 'customers' :
                  typeLower === 'lead' ? 'leads' :
                  typeLower === 'opportunity' ? 'opportunities' :
                  typeLower === 'activity' ? 'activities' :
                  typeLower === 'note' ? 'notes' :
                  typeLower;
    
    return { id, route: `/${route}/${id}` };
  };

  // Get additional info for display
  const getEntityInfo = (item, type, filterColumns = []) => {
    const typeLower = (type || '').toLowerCase();
    const info = [];
    
    // Helper to check if a field should be shown (always show if it's in filterColumns)
    const shouldShowField = (fieldName) => {
      const fieldLower = fieldName.toLowerCase();
      return filterColumns.some(fc => fc.toLowerCase() === fieldLower);
    };
    
    switch (typeLower) {
      case 'customer':
        // Always show Email if it's in filterColumns or if it exists
        if (shouldShowField('Email') || item.Email) info.push(`📧 ${item.Email}`);
        // Always show Phone if it's in filterColumns or if it exists
        if (shouldShowField('Phone') || item.Phone) info.push(`📞 ${item.Phone}`);
        // Always show NickName if it's in filterColumns
        if (shouldShowField('NickName') && item.NickName) info.push(`👤 Nickname: ${item.NickName}`);
        if (item.CompanyName) info.push(`🏢 ${item.CompanyName}`);
        break;
      case 'lead':
        // Show ContactName/CompanyName if not already in the name
        if (item.ContactName && !item.Title?.includes(item.ContactName)) {
          info.push(`👤 ${item.ContactName}`);
        } else if ((item.CompanyName || item.Company) && !item.Title?.includes(item.CompanyName || item.Company)) {
          info.push(`🏢 ${item.CompanyName || item.Company}`);
        }
        // Always show Email if it's in filterColumns or if it exists
        if (shouldShowField('Email') || item.Email) info.push(`📧 ${item.Email}`);
        // Always show Phone if it's in filterColumns or if it exists
        if (shouldShowField('Phone') || item.Phone) info.push(`📞 ${item.Phone}`);
        // Always show EstimatedBudget/EstimatedValue if it's in filterColumns or if it exists
        if (shouldShowField('EstimatedBudget') || shouldShowField('EstimatedValue') || item.EstimatedBudget || item.EstimatedValue) {
          const value = item.EstimatedBudget || item.EstimatedValue;
          const displayValue = typeof value === 'number' ? `$${value.toLocaleString()}` : value;
          info.push(`💰 Value: ${displayValue}`);
        }
        if (item.Status) info.push(`📊 ${item.Status}`);
        break;
      case 'opportunity':
        if (item.Value) {
          const value = typeof item.Value === 'number' ? `$${item.Value.toLocaleString()}` : item.Value;
          info.push(`💰 ${value}`);
        }
        if (item.Stage) info.push(`📊 ${item.Stage}`);
        break;
      case 'activity':
        if (item.ActivityType) info.push(`📋 ${item.ActivityType}`);
        if (item.Status) info.push(`📊 ${item.Status}`);
        if (item.DueDate) info.push(`📅 ${new Date(item.DueDate).toLocaleDateString()}`);
        break;
      default:
        // Show first few non-ID fields
        Object.entries(item).slice(0, 2).forEach(([key, value]) => {
          if (value !== null && value !== undefined && 
              !key.toLowerCase().includes('id') && 
              !key.toLowerCase().includes('name') &&
              !key.toLowerCase().includes('subject')) {
            info.push(`${key}: ${String(value).substring(0, 30)}`);
          }
        });
    }
    
    return info;
  };

  const Icon = entityIcons[entityType?.toLowerCase()] || FileText;
  const displayType = entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Entity';
  const displayedData = data.slice(0, 20); // Show up to 20 items in list

  return (
    <div className="entity-data-list my-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {displayType} Results
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({totalCount} {totalCount === 1 ? 'item' : 'items'})
          </span>
        </div>
      </div>
      
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
        {displayedData.map((item, index) => {
          const entityName = getEntityName(item, entityType, filterColumns);
          const entityRoute = getEntityRoute(item, entityType);
          const entityInfo = getEntityInfo(item, entityType, filterColumns);
          
          return (
            <div
              key={index}
              className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {entityName}
                    </span>
                  </div>
                  {entityInfo.length > 0 && (
                    <div className="ml-6 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                      {entityInfo.slice(0, 2).map((info, infoIdx) => (
                        <div key={infoIdx}>{info}</div>
                      ))}
                    </div>
                  )}
                </div>
                {entityRoute && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(entityRoute.route);
                    }}
                    className="ml-3 flex-shrink-0 text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1 cursor-pointer bg-transparent border-none p-0"
                  >
                    <span>View</span>
                    <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {totalCount > displayedData.length && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
          Showing {displayedData.length} of {totalCount} results
        </div>
      )}
    </div>
  );
};

export default EntityDataList;

